import { cn } from "@/lib/utils";
import type { FilterOption } from "@/lib/constants";

/* ─── Filter Pills ─── */
function FilterPills<T extends string>({
  options,
  value,
  onChange,
  allLabel,
}: {
  options: FilterOption<T>[];
  value: T | undefined;
  onChange: (v: T | undefined) => void;
  allLabel: string;
}) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-1">
      <button
        onClick={() => onChange(undefined)}
        className={cn(
          "px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all",
          !value
            ? "bg-foreground text-background"
            : "bg-muted text-muted-foreground hover:text-foreground",
        )}
      >
        {allLabel}
      </button>
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          className={cn(
            "px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all flex items-center gap-1.5",
            value === opt.id
              ? "bg-foreground text-background"
              : "bg-muted text-muted-foreground hover:text-foreground",
          )}
        >
          {opt.icon && <opt.icon className="w-3.5 h-3.5" />}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Filter Bar ─── */
import { POST_STATUS_FILTERS, PLATFORM_FILTERS } from "@/lib/constants";
import { Platform, PostStatus } from "@/types";
import type { FeedFilters } from "@/types";

interface FilterBarProps {
  filters: FeedFilters;
  onChange: (filters: FeedFilters) => void;
}

export function FilterBar({ filters, onChange }: FilterBarProps) {
  return (
    <div className="px-4 py-2 space-y-2 border-b bg-background/80 backdrop-blur-sm sticky top-[53px] z-30">
      <FilterPills
        options={POST_STATUS_FILTERS}
        value={filters.status}
        onChange={(v) => onChange({ ...filters, status: v as PostStatus })}
        allLabel="All"
      />
      <FilterPills
        options={PLATFORM_FILTERS}
        value={filters.platform}
        onChange={(v) => onChange({ ...filters, platform: v as Platform | undefined })}
        allLabel="All Platforms"
      />
    </div>
  );
}
